// useModuleMetrics.js
// ─────────────────────────────────────────────────────────────────────────────
//
// Manages all data-fetching for the Module Metrics page.
//
// ── Responsibilities ──────────────────────────────────────────────────────────
//
//   1. Fetch the full modules list on mount (modules-list route) and cache it
//      for the lifetime of the component.
//
//   2. Expose a filtered + sorted dropdown list derived from that cache:
//        • Before the user types  → top 10 modules, sorted by actualOnlineDate
//          descending (most recently online first).
//        • While the user types   → modules whose WoNum, ItemNum, or BatchNum
//          contains the query string (case-insensitive).
//
//   3. On module selection: call the module-metrics route with the selected
//      woNum. Cancels any in-flight request if the user switches modules.
//
//   4. On module selection: call the activity-module route with the selected
//      woNum in parallel with the module-metrics fetch. Each route uses its
//      own AbortController so cancellation is independent and clean.
//
//   5. URL sync: if ?workOrder=<WoNum> is present on mount, auto-select that
//      module once the list has loaded and strip the param from the URL.
//
// ── Return shape ──────────────────────────────────────────────────────────────
//
//   {
//     // Search / dropdown controls
//     search:           string,
//     setSearch:        (value: string) => void,
//     dropdownOpen:     boolean,
//     setDropdownOpen:  (open: boolean) => void,
//     dropdownModules:  Module[],          - filtered list for the dropdown
//
//     // Selection
//     selectedModule:   Module | null,     - the raw modules-list row
//     handleSelectModule: (mod: Module) => void,
//     handleClearSelection: () => void,
//
//     // Module-metrics fetch state
//     moduleData:       ModuleMetricsPayload | null,
//     metricsLoading:   boolean,
//     metricsError:     string | null,
//
//     // Activity-history fetch state
//     activityData:     ActivityPayload | null,   - { jobs, count }
//     activityLoading:  boolean,
//     activityError:    string | null,
//
//     // Modules-list fetch state
//     listLoading:      boolean,
//     listError:        string | null,
//   }
//
// ── Type aliases (JSDoc only - no TS runtime) ─────────────────────────────────
//
//   Module = {
//     WoNum: string, ItemNum: string, BatchNum: string,
//     scheduledOnlineDate: string, actualOnlineDate: string | null,
//     scheduledOfflineDate: string | null, Sqft: number | null,
//     Property: string | null,
//   }
//
//   ModuleMetricsPayload = return type of fetchModuleMetrics (see dataReceiver.js)
//   ActivityPayload      = return type of fetchActivityModule (see dataReceiver.js)

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchModulesList,
  fetchModuleMetrics,
  fetchActivityModule,
} from "../../utils/dataReceiver";

// ── Constants ─────────────────────────────────────────────────────────────────

/** How many modules to show in the dropdown before the user starts typing. */
const DROPDOWN_DEFAULT_LIMIT = 10;

// ─────────────────────────────────────────────────────────────────────────────

export function useModuleMetrics() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Modules list ────────────────────────────────────────────────────────────
  /** @type {[import('../dataReceiver').Module[], Function]} */
  const [modulesList, setModulesList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // ── Search / dropdown ───────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  // ── Metrics fetch state ─────────────────────────────────────────────────────
  const [moduleData, setModuleData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  // ── Activity-history fetch state ────────────────────────────────────────────
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  // AbortController refs - each route gets its own ref so cancelling one does
  // not affect the other. Both are cancelled on clear/unmount.
  const metricsAbortRef = useRef(null);
  const activityAbortRef = useRef(null);

  // ── 1. Fetch modules list on mount ─────────────────────────────────────────
  //
  // Uses a separate AbortController so the list fetch is cancelled cleanly on
  // unmount without touching the metrics/activity abort refs.
  useEffect(() => {
    const controller = new AbortController();

    async function loadList() {
      setListLoading(true);
      setListError(null);
      try {
        const rows = await fetchModulesList(controller.signal);
        if (!controller.signal.aborted) {
          setModulesList(rows);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setListError(
          err?.message ?? "Failed to load the modules list. Please refresh.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setListLoading(false);
        }
      }
    }

    loadList();
    return () => controller.abort();
  }, []);

  // ── 2. URL param auto-selection ─────────────────────────────────────────────
  //
  // Fires once the modules list is populated and a ?workOrder= param exists.
  // Matches on WoNum (exact, case-insensitive). Strips the param from the URL
  // after selection so back/forward navigation doesn't re-trigger it.
  useEffect(() => {
    if (listLoading || modulesList.length === 0) return;

    const workOrderParam = searchParams.get("workOrder");
    if (!workOrderParam || selectedModule) return;

    const match = modulesList.find(
      (m) => m.WoNum?.toLowerCase() === workOrderParam.toLowerCase(),
    );

    if (match) {
      setSearch(match.WoNum);
      setSelectedModule(match);
      setSearchParams({}, { replace: true });
    }
  }, [listLoading, modulesList, searchParams, selectedModule, setSearchParams]);

  // ── 3. Metrics + activity fetches on selection ─────────────────────────────
  //
  // Both fetches are triggered whenever selectedModule changes to a non-null
  // value. Each has its own AbortController so they cancel independently, but
  // both are cancelled together when the user switches modules or clears.
  useEffect(() => {
    if (!selectedModule) {
      setModuleData(null);
      setMetricsError(null);
      setActivityData(null);
      setActivityError(null);
      return;
    }

    // ── 3a. Module metrics ──────────────────────────────────────────────────

    metricsAbortRef.current?.abort();
    const metricsController = new AbortController();
    metricsAbortRef.current = metricsController;

    async function loadMetrics() {
      setMetricsLoading(true);
      setMetricsError(null);
      setModuleData(null);
      try {
        const data = await fetchModuleMetrics(
          { woNum: selectedModule.WoNum },
          metricsController.signal,
        );
        if (!metricsController.signal.aborted) {
          setModuleData(data);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setMetricsError(
          err?.message ??
            `Failed to load metrics for ${selectedModule.WoNum}. Please try again.`,
        );
      } finally {
        if (!metricsController.signal.aborted) {
          setMetricsLoading(false);
        }
      }
    }

    // ── 3b. Activity history ────────────────────────────────────────────────

    activityAbortRef.current?.abort();
    const activityController = new AbortController();
    activityAbortRef.current = activityController;

    async function loadActivity() {
      setActivityLoading(true);
      setActivityError(null);
      setActivityData(null);
      try {
        const data = await fetchActivityModule(
          { woNum: selectedModule.WoNum },
          activityController.signal,
        );
        if (!activityController.signal.aborted) {
          setActivityData(data);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setActivityError(
          err?.message ??
            `Failed to load activity history for ${selectedModule.WoNum}. Please try again.`,
        );
      } finally {
        if (!activityController.signal.aborted) {
          setActivityLoading(false);
        }
      }
    }

    // Fire both fetches in parallel - neither blocks the other.
    loadMetrics();
    loadActivity();

    return () => {
      metricsController.abort();
      activityController.abort();
    };
  }, [selectedModule]);

  // Abort any in-flight fetches on unmount
  useEffect(() => {
    return () => {
      metricsAbortRef.current?.abort();
      activityAbortRef.current?.abort();
    };
  }, []);

  // ── 4. Dropdown list derivation ─────────────────────────────────────────────
  //
  // Before typing:  top DROPDOWN_DEFAULT_LIMIT modules sorted by
  //                 actualOnlineDate descending (most recent on-line first).
  //                 Modules with no actualOnlineDate sort to the end.
  //
  // While typing:   any module whose WoNum, ItemNum, or BatchNum contains the
  //                 trimmed query (case-insensitive). No limit applied so the
  //                 user can see all matches.
  const dropdownModules = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) {
      // Default view - sort by most recently online, cap at limit
      return [...modulesList]
        .sort((a, b) => {
          const da = a.actualOnlineDate
            ? new Date(a.actualOnlineDate).getTime()
            : 0;
          const db = b.actualOnlineDate
            ? new Date(b.actualOnlineDate).getTime()
            : 0;
          return db - da;
        })
        .slice(0, DROPDOWN_DEFAULT_LIMIT);
    }

    return modulesList.filter(
      (m) =>
        m.WoNum?.toLowerCase().includes(q) ||
        m.ItemNum?.toLowerCase().includes(q) ||
        m.BatchNum?.toLowerCase().includes(q),
    );
  }, [modulesList, search]);

  // ── 5. Interaction handlers ─────────────────────────────────────────────────

  /**
   * Called when the user clicks a dropdown item.
   * Updates search text to the WoNum and closes the dropdown.
   */
  const handleSelectModule = useCallback((mod) => {
    setSelectedModule(mod);
    setSearch(mod.WoNum);
    setDropdownOpen(false);
  }, []);

  /**
   * Called when the user clicks the ✕ clear button.
   * Resets all selection and search state; cancels any in-flight fetches.
   */
  const handleClearSelection = useCallback(() => {
    metricsAbortRef.current?.abort();
    activityAbortRef.current?.abort();
    setSelectedModule(null);
    setModuleData(null);
    setMetricsError(null);
    setActivityData(null);
    setActivityError(null);
    setSearch("");
    setDropdownOpen(false);
  }, []);

  // ── Return ──────────────────────────────────────────────────────────────────

  return {
    // Search / dropdown
    search,
    setSearch,
    dropdownOpen,
    setDropdownOpen,
    dropdownModules,

    // Selection
    selectedModule,
    handleSelectModule,
    handleClearSelection,

    // Metrics fetch state
    moduleData,
    metricsLoading,
    metricsError,

    // Activity-history fetch state
    activityData,
    activityLoading,
    activityError,

    // List fetch state
    listLoading,
    listError,
  };
}
